// Phase 1.5 (hh-vkh) — AISummary bubble.
// "Done — recorded" reply: short summary text + inline list of agent actions
// (create / move / delete) + buttons Undo / In tree / Add more.
//
// Ported from designproject/files/screens-feed.jsx (AISummary). Diffs vs the
// source JSX:
//   • the source's `needsCompletion` / `completionHint` / Дополнить affordance
//     is NOT carried over — it belongs to the "incomplete record" flow which
//     is a separate later phase. Phase 1.5 ships only the canonical summary.
//   • Undo only fires when `undoable && agentActionId` are both present
//     (server-side 24h window).
//
// Wiring to /api/v1/agent-actions/{id}/undo lives in hh-46n — this component
// just emits the id via onUndo.

import type { JSX } from 'preact';
import { T, Icon, Btn, Avatar } from '../../tokens';
import type { IconName } from '../../tokens';
import type { AISummaryAction, AISummaryMessage } from './Message';

export interface AISummaryProps {
  readonly msg: AISummaryMessage;
  readonly onUndo?: (agentActionId: string) => void;
  readonly onOpenTree?: () => void;
  readonly onAddMore?: () => void;
}

const ACTION_ICON: Record<AISummaryAction['kind'], IconName> = {
  create: 'plus',
  move: 'move',
  delete: 'trash',
};

function ActionRow({ action }: { readonly action: AISummaryAction }): JSX.Element {
  const icon = ACTION_ICON[action.kind];
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.6)',
        borderRadius: 10,
        padding: '8px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        fontWeight: 600,
        color: T.ink,
      }}
    >
      <Icon name={icon} size={14} color={T.brandDeep} />
      <span style={{ flex: 1 }}>
        {action.name}
        {action.kind === 'move' && action.from !== undefined && action.to !== undefined && (
          <span style={{ color: T.ink3, fontWeight: 500 }}>
            {' · '}
            {action.from}
            {' → '}
            {action.to}
          </span>
        )}
        {action.kind !== 'move' && action.to !== undefined && (
          <span style={{ color: T.ink3, fontWeight: 500 }}> → {action.to}</span>
        )}
      </span>
    </div>
  );
}

export function AISummary({ msg, onUndo, onOpenTree, onAddMore }: AISummaryProps): JSX.Element {
  const hasActions = msg.actions.length > 0;
  // Undo is only meaningful when both server flag and id are present.
  const canUndo = msg.undoable && msg.agentActionId !== undefined;
  const handleUndo = canUndo
    ? () => {
        if (msg.agentActionId !== undefined) onUndo?.(msg.agentActionId);
      }
    : undefined;

  return (
    <div style={{ padding: '4px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <Avatar kind="ai" icon="spark" size={28} />
      <div style={{ minWidth: 0, maxWidth: '78%' }}>
        <div
          style={{
            background: T.brandSoft,
            border: `1px solid ${T.brandSoft2}`,
            padding: '12px 14px',
            borderRadius: '4px 18px 18px 18px',
            fontSize: 14.5,
            lineHeight: 1.4,
            color: T.ink,
            fontWeight: 500,
          }}
        >
          <div style={{ marginBottom: hasActions ? 10 : 0 }}>{msg.text}</div>

          {hasActions && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {msg.actions.map((a, i) => (
                <ActionRow key={`${a.kind}-${a.itemId ?? i}`} action={a} />
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {canUndo && handleUndo !== undefined && (
              <Btn kind="outline" size="sm" icon="undo" onClick={handleUndo}>
                Отменить
              </Btn>
            )}
            {onOpenTree !== undefined && (
              <Btn kind="ghost" size="sm" onClick={onOpenTree}>
                В дереве
              </Btn>
            )}
            {onAddMore !== undefined && (
              <Btn kind="ghost" size="sm" icon="plus" onClick={onAddMore}>
                Добавить ещё
              </Btn>
            )}
          </div>
        </div>
        {msg.time !== undefined && (
          <div style={{ fontSize: 11, color: T.ink3, marginTop: 4, fontWeight: 500 }}>
            {msg.time}
          </div>
        )}
      </div>
    </div>
  );
}
